import React, { useState, useEffect } from 'react';
import {
  Arrange,
  Button,
  Container,
  FormField,
  Spacer,
  Split,
  SplitSection,
  SvgTrash,
  TextButton,
  TextInput,
} from '@loomhq/lens';
import { useLayer } from 'react-laag';
import { ENTER, ESCAPE } from 'constants/keyCodes';
import { useDispatch } from 'react-redux';
import {
  toggleCorrectionPopup,
  CORRECTION_POPUP_ANCHOR_ID,
} from 'reducers/transcription';

const DUMMY_BOUNDS = {
  bottom: 0,
  height: 0,
  left: 0,
  right: 0,
  top: 0,
  width: 0,
  x: -2000,
  y: -2000,
};

const TranscriptCorrectionPopup = ({
  value,
  onCorrectOne,
  onCorrectAll,
  onDeleteWord,
}: {
  value: string;
  onCorrectOne: (newValue, original) => void;
  onCorrectAll: (newValue, original) => void;
  onDeleteWord: (original) => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const [correction, setCorrection] = useState(value);
  const isInputValid = correction !== '';

  const hidePopup = () => {
    dispatch(toggleCorrectionPopup(false));
  };

  useEffect(() => {
    setCorrection(value);
  }, [value]);

  const { layerProps, renderLayer } = useLayer({
    isOpen: true,
    placement: 'bottom-center',
    triggerOffset: 16,
    auto: true,
    trigger: {
      getBounds: () => {
        const anchorElement = document?.getElementById(
          CORRECTION_POPUP_ANCHOR_ID
        );

        if (anchorElement) {
          return anchorElement.getBoundingClientRect();
        }

        return DUMMY_BOUNDS;
      },
    },
    onOutsideClick: hidePopup,
  });

  const adjustedLayerProps = {
    ...layerProps,
    style: { ...layerProps.style, zIndex: 3 },
  };

  const handleCorrectionInputChange = ev => {
    ev.stopPropagation();
    // do not accept anything other than word chars
    setCorrection(ev.target.value.replace(/[^\w'-]/g, ''));
  };

  const handleCorrectClick = () => {
    onCorrectOne?.(correction, value);
    hidePopup();
  };

  const handleCorrectAllClick = () => {
    onCorrectAll?.(correction, value);
    hidePopup();
  };

  const handleDeleteWordClick = () => {
    onDeleteWord?.(value);
    hidePopup();
  };

  const handleKeyUp = ev => {
    if (ev.keyCode === ESCAPE) {
      ev.stopPropagation();
      hidePopup();
    } else if (isInputValid && ev.keyCode === ENTER) {
      // default behavior is to correct only the selected occurrence
      handleCorrectClick();
    }
  };

  return (
    <>
      {renderLayer(
        <div className="tooltip" {...adjustedLayerProps}>
          <Container
            position="relative"
            width="max-content"
            height="max-content"
            backgroundColor="overlay"
            borderColor="border"
            padding="12px"
            borderSide="all"
            radius="medium"
            borderWidth="1px"
            shadow="medium"
          >
            <FormField label="" labelFor="correction">
              <TextInput
                id="correction"
                autoFocus={true}
                value={correction}
                onChange={handleCorrectionInputChange}
                onKeyUp={handleKeyUp}
              />
            </FormField>
            <Spacer top={1} />
            <Split wrap="nowrap" gap="medium" justifyContent="space-between">
              <SplitSection>
                <TextButton
                  size="small"
                  icon={<SvgTrash />}
                  onClick={handleDeleteWordClick}
                >
                  Delete word
                </TextButton>
              </SplitSection>
              <SplitSection>
                <Arrange gap={1}>
                  <Button
                    variant="neutral"
                    size="small"
                    isDisabled={!isInputValid}
                    onClick={handleCorrectAllClick}
                  >
                    Correct all
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    isDisabled={!isInputValid}
                    onClick={handleCorrectClick}
                  >
                    Correct
                  </Button>
                </Arrange>
              </SplitSection>
            </Split>
          </Container>
        </div>
      )}
    </>
  );
};

export default TranscriptCorrectionPopup;
